import type { FragmentType } from 'src/gql';
import type { AssetFields } from 'src/types/asset';
import { Label } from '@savvy-kit/components/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@savvy-kit/components/select';
import { DateField } from '@savvy-kit/patterns/date-field';
import InputField from '@savvy-kit/patterns/input-field';
import { useFormik } from 'formik';
import { DateTime } from 'luxon';
import MonetaryInputTextField from 'src/components/mui/monetary-input-text-field';
import TrackedButton from 'src/components/tracked/tracked-button';
import { graphql, unmaskFragment } from 'src/gql';
import { getFormattedEOD, isValidDate } from 'src/utils/validation';
import { date as yupDate, number as yupNumber, object as yupObject, string as yupString } from 'yup';

export const CompanyEquityGrantDetailsFormFragment = graphql(/* GraphQL */ `
  fragment CompanyEquityGrantDetailsForm on Asset {
    companyEquityGrantType
    companyEquitySharesIssued
    companyEquityExercisePrice
    companyEquityVestingStartDate
    companyEquityVestingDuration
    companyEquityVestingCliff
    companyEquityVestingRate
  }
`);

const enum CompanyEquityGrantType {
  ISO = 'ISO',
  NSO = 'NSO',
  RSU = 'RSU',
}

const enum CompanyEquityGrantVestingRates {
  DAILY = 'Daily',
  WEEKLY = 'Weekly',
  MONTHLY = 'Monthly',
  QUARTERLY = 'Quarterly',
  YEARLY = 'Yearly',
}

function toVestingStartDate(stored: string | null): Date | undefined {
  if (!stored) return undefined;
  const parsed = DateTime.fromISO(stored);
  return parsed.isValid ? parsed.toJSDate() : undefined;
}

export default function CompanyEquityGrantDetailsForm({
  assetFrag,
  assetFields,
  isEditMode,
  onNext,
}: {
  readonly assetFrag?: FragmentType<typeof CompanyEquityGrantDetailsFormFragment>;
  readonly assetFields?: AssetFields;
  readonly isEditMode: boolean;
  readonly onNext: (assetFields: AssetFields) => void;
}) {
  const asset = unmaskFragment(CompanyEquityGrantDetailsFormFragment, assetFrag);
  const today = new Date();
  const dateString = '1900-01-01';
  const dateMin = DateTime.fromISO(dateString).toString();
  const formik = useFormik({
    initialValues: {
      companyEquityGrantType: assetFields?.companyEquityGrantType ?? asset?.companyEquityGrantType ?? '',
      companyEquitySharesIssued: assetFields?.companyEquitySharesIssued ?? asset?.companyEquitySharesIssued ?? '',
      companyEquityExercisePrice: assetFields?.companyEquityExercisePrice ?? asset?.companyEquityExercisePrice ?? '',
      companyEquityVestingStartDate:
        (assetFields?.companyEquityVestingStartDate ?? asset?.companyEquityVestingStartDate)
          ? DateTime.fromISO(
              assetFields?.companyEquityVestingStartDate ?? asset?.companyEquityVestingStartDate ?? '',
            ).toString()
          : null,
      companyEquityVestingDuration:
        assetFields?.companyEquityVestingDuration ?? asset?.companyEquityVestingDuration ?? '',
      companyEquityVestingCliff: assetFields?.companyEquityVestingCliff ?? asset?.companyEquityVestingCliff ?? '',
      companyEquityVestingRate: assetFields?.companyEquityVestingRate ?? asset?.companyEquityVestingRate ?? '',
    },
    validateOnBlur: true,
    validationSchema: yupObject({
      companyEquityGrantType: yupString().required('Grant type is required'),
      companyEquitySharesIssued: yupNumber()
        .positive('Number of shares must be a positive number')
        .required('Number of shares required'),
      companyEquityVestingStartDate: yupDate()
        .required('Enter a valid vesting start date')
        .min(dateString, `Date must be later than ${dateMin}`)
        .max(getFormattedEOD(), "Date can't be in the future."),
      // Not required because not relevant for RSU
      companyEquityExercisePrice: yupNumber().positive('Exercise price must be a positive number'),
      companyEquityVestingCliff: yupNumber()
        .min(0, 'Vesting cliff cannot be negative')
        .required('Vesting cliff is required'),
      companyEquityVestingDuration: yupNumber()
        .positive('Vesting duration must be a positive number')
        .required('Vesting duration is required'),
      companyEquityVestingRate: yupString().required('Vesting rate is required'),
    }),
    onSubmit: () => {
      onNext(formik.values);
    },
  });

  return (
    <form noValidate onSubmit={formik.handleSubmit}>
      <div className="mt-4 mb-2 flex flex-col gap-2">
        <Label htmlFor="companyEquityGrantType">Grant type</Label>
        <Select
          value={formik.values.companyEquityGrantType}
          items={[CompanyEquityGrantType.ISO, CompanyEquityGrantType.NSO, CompanyEquityGrantType.RSU].map(
            (companyEquityGrantType) => ({ value: companyEquityGrantType, label: companyEquityGrantType }),
          )}
          onValueChange={(value) => {
            formik.setFieldValue('companyEquityGrantType', value);
          }}
        >
          <SelectTrigger
            id="companyEquityGrantType"
            className="w-full"
            autoFocus={!formik.values.companyEquityGrantType}
            onBlur={() => formik.setFieldTouched('companyEquityGrantType', true)}
            aria-describedby={
              formik.touched.companyEquityGrantType && formik.errors.companyEquityGrantType
                ? 'company-equity-grant-type-error'
                : undefined
            }
          >
            <SelectValue placeholder="Select..." />
          </SelectTrigger>
          <SelectContent>
            {[CompanyEquityGrantType.ISO, CompanyEquityGrantType.NSO, CompanyEquityGrantType.RSU].map(
              (companyEquityGrantType) => (
                <SelectItem key={companyEquityGrantType} value={companyEquityGrantType}>
                  {companyEquityGrantType}
                </SelectItem>
              ),
            )}
          </SelectContent>
        </Select>
        {formik.touched.companyEquityGrantType && formik.errors.companyEquityGrantType ? (
          <p id="company-equity-grant-type-error" className="text-destructive text-sm">
            {formik.errors.companyEquityGrantType}
          </p>
        ) : null}
      </div>
      {formik.values.companyEquityGrantType && (
        <>
          <InputField
            id="companyEquitySharesIssued"
            containerClassName="mt-4 mb-2"
            errorMessage={formik.errors.companyEquitySharesIssued}
            min={1}
            error={Boolean(formik.touched.companyEquitySharesIssued && formik.errors.companyEquitySharesIssued)}
            label="Number of shares issued"
            name="companyEquitySharesIssued"
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            type="number"
            value={formik.values.companyEquitySharesIssued}
          />
          {[CompanyEquityGrantType.ISO, CompanyEquityGrantType.NSO].includes(
            // @ts-expect-error - FIXME: This is using the wrong type
            formik.values.companyEquityGrantType,
          ) && (
            <MonetaryInputTextField
              error={Boolean(formik.touched.companyEquityExercisePrice && formik.errors.companyEquityExercisePrice)}
              errorMessage={formik.errors.companyEquityExercisePrice}
              label="Exercise price"
              name="companyEquityExercisePrice"
              onBlur={formik.handleBlur}
              formik={formik}
              value={formik.values.companyEquityExercisePrice}
            />
          )}
          <DateField
            id="companyEquityVestingStartDate"
            label="Vesting start date"
            maxDate={today}
            value={toVestingStartDate(formik.values.companyEquityVestingStartDate)}
            onChange={(value) => {
              const validDate = isValidDate(value);
              if (validDate) {
                formik.setFieldValue('companyEquityVestingStartDate', DateTime.fromJSDate(value).toISODate(), true);
              } else {
                formik.setFieldValue('companyEquityVestingStartDate', '', false);
              }
            }}
            onBlur={() => formik.setFieldTouched('companyEquityVestingStartDate', true)}
            error={Boolean(formik.touched.companyEquityVestingStartDate && formik.errors.companyEquityVestingStartDate)}
            errorMessage={
              formik.touched.companyEquityVestingStartDate && formik.errors.companyEquityVestingStartDate
                ? 'Please enter a valid date'
                : undefined
            }
          />
          <InputField
            id="companyEquityVestingDuration"
            containerClassName="mt-4 mb-2"
            errorMessage={formik.errors.companyEquityVestingDuration}
            min={1}
            error={Boolean(formik.touched.companyEquityVestingDuration && formik.errors.companyEquityVestingDuration)}
            label="Vesting duration (years)"
            name="companyEquityVestingDuration"
            onBlur={formik.handleBlur}
            onChange={(e) => {
              const value = e.target.value;
              // Only allow integer >= 1
              if (value === '' || /^[1-9]\d*$/.test(value)) {
                formik.handleChange(e);
              }
            }}
            type="number"
            value={formik.values.companyEquityVestingDuration}
          />
          <InputField
            id="companyEquityVestingCliff"
            containerClassName="mt-4 mb-2"
            errorMessage={formik.errors.companyEquityVestingCliff}
            min={0}
            error={Boolean(formik.touched.companyEquityVestingCliff && formik.errors.companyEquityVestingCliff)}
            label="Vesting cliff (years)"
            name="companyEquityVestingCliff"
            onBlur={formik.handleBlur}
            onChange={(e) => {
              const value = e.target.value;
              // Only allow integer >= 1
              if (value === '' || /^[1-9]\d*$/.test(value)) {
                formik.handleChange(e);
              }
            }}
            type="number"
            value={formik.values.companyEquityVestingCliff}
          />
          <div className="mt-4 mb-2 flex flex-col gap-2">
            <Label htmlFor="companyEquityVestingRate">Vesting rate</Label>
            <Select
              value={formik.values.companyEquityVestingRate}
              items={[
                CompanyEquityGrantVestingRates.DAILY,
                CompanyEquityGrantVestingRates.WEEKLY,
                CompanyEquityGrantVestingRates.MONTHLY,
                CompanyEquityGrantVestingRates.QUARTERLY,
                CompanyEquityGrantVestingRates.YEARLY,
              ].map((companyEquityVestingRate) => ({
                value: companyEquityVestingRate,
                label: companyEquityVestingRate,
              }))}
              onValueChange={(value) => {
                formik.setFieldValue('companyEquityVestingRate', value);
              }}
            >
              <SelectTrigger
                id="companyEquityVestingRate"
                className="w-full"
                onBlur={() => formik.setFieldTouched('companyEquityVestingRate', true)}
                aria-describedby={
                  formik.touched.companyEquityVestingRate && formik.errors.companyEquityVestingRate
                    ? 'company-equity-vesting-rate-error'
                    : undefined
                }
              >
                <SelectValue placeholder="Select..." />
              </SelectTrigger>
              <SelectContent>
                {[
                  CompanyEquityGrantVestingRates.DAILY,
                  CompanyEquityGrantVestingRates.WEEKLY,
                  CompanyEquityGrantVestingRates.MONTHLY,
                  CompanyEquityGrantVestingRates.QUARTERLY,
                  CompanyEquityGrantVestingRates.YEARLY,
                ].map((companyEquityVestingRate) => (
                  <SelectItem key={companyEquityVestingRate} value={companyEquityVestingRate}>
                    {companyEquityVestingRate}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {formik.touched.companyEquityVestingRate && formik.errors.companyEquityVestingRate ? (
              <p id="company-equity-vesting-rate-error" className="text-destructive text-sm">
                {formik.errors.companyEquityVestingRate}
              </p>
            ) : null}
          </div>
        </>
      )}

      <div className="flex flex-row justify-end mt-4 mb-6">
        <TrackedButton
          elementName={
            isEditMode
              ? 'update-company-equity-grant-details-form-next-button'
              : 'create-company-equity-grant-details-form-next-button'
          }
          disabled={!isEditMode && (formik.isSubmitting || !formik.isValid)}
          type="submit"
        >
          Next
        </TrackedButton>
      </div>
    </form>
  );
}
